import {getConfig} from "../lib/configMgr";
import {RequestBuilder, toJSON} from "./request";
import {SearchCanceled, SearchError, SearchTimeoutError} from "./errors";
import {constructSearchAPIRequest} from "../lib/SearchContext";
import {getCustomLabels, wait} from "../lib/utils";
import {toast} from "react-toastify";

const { ExternalRESTAPI } = getConfig();

// Default retry configuration for derivative operations (graph, echains, connected, export)
// These operations depend on cached search results which may expire or be evicted
const DEFAULT_RETRY_CONFIG = {
  maxRetries: 3,
  initialDelayMs: 2000,
  maxDelayMs: 10000,
  backoffMultiplier: 1.5,
};

/**
 * Constructs the SSE stream URL from a stream_id
 * @param {string} streamId - The stream ID returned from the search API
 * @returns {string} The full stream URL
 */
export const getStreamURL = (streamId) => {
  if (!streamId) return null;
  return ExternalRESTAPI.Stream.indexOf("{streamId}") !== -1
    ? ExternalRESTAPI.Stream.replace("{streamId}", streamId)
    : `${ExternalRESTAPI.Stream}?channel=${streamId}`;
};

/**
 * PLEX-6261: a stream's own SSE path, GET /api/stream/{id}/sse. A backend deployed
 * before that path answers it 404; getStreamURL is then the form to connect on (SSE by
 * its Accept header), which is what ResumableEventSource's fallbackUrl is for.
 * useStreamConnector builds the same URL for its SSE leg.
 */
export const getStreamSseURL = (streamId) => {
  if (!streamId) return null;
  return ExternalRESTAPI.Stream.indexOf("{streamId}") !== -1
    ? `${ExternalRESTAPI.Stream.replace("{streamId}", streamId)}/sse`
    : getStreamURL(streamId);
};

/**
 * Execute a fetch with retry logic for operations that depend on cached search results.
 * Retries on 202 (search still running) or cache miss scenarios.
 *
 * @param {Function} fetchFn - Async function that performs the fetch
 * @param {Object} options - Retry options
 * @param {number} options.maxRetries - Maximum number of retry attempts
 * @param {number} options.initialDelayMs - Initial delay between retries
 * @param {number} options.maxDelayMs - Maximum delay between retries
 * @param {number} options.backoffMultiplier - Multiplier for exponential backoff
 * @param {Function} options.isCanceled - Function that returns true if operation should be canceled
 * @param {Function} options.onRetry - Callback called before each retry with (attempt, delay, reason)
 * @param {Object} options.searchParams - Original search params (required for cache miss recovery)
 * @param {Object} options.auth - Authentication data (required for cache miss recovery)
 * @param {Function} options.onCacheMissRecovery - Callback called when cache miss recovery is attempted
 * @returns {Promise} - Resolves with the fetch result or rejects after all retries exhausted
 */
const fetchWithRetry = async (fetchFn, options = {}) => {
  const {
    maxRetries = DEFAULT_RETRY_CONFIG.maxRetries,
    initialDelayMs = DEFAULT_RETRY_CONFIG.initialDelayMs,
    maxDelayMs = DEFAULT_RETRY_CONFIG.maxDelayMs,
    backoffMultiplier = DEFAULT_RETRY_CONFIG.backoffMultiplier,
    isCanceled = () => false,
    onRetry = null,
    searchParams = null,
    auth = null,
    onCacheMissRecovery = null,
  } = options;

  let lastError = null;
  let delay = initialDelayMs;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    if (isCanceled()) {
      throw new SearchCanceled("Operation canceled");
    }

    try {
      const result = await fetchFn();

      // Check if we got a 202 (search still running) - need to retry
      if (result?.status === 202) {
        lastError = new Error(result.info || "Search still in progress");
        lastError.status = 202;

        if (attempt < maxRetries) {
          if (onRetry) {
            onRetry(attempt + 1, delay, "Search still in progress");
          }
          await wait(delay);
          delay = Math.min(delay * backoffMultiplier, maxDelayMs);
          continue;
        }
      }

      // Check for 404 cache miss - search results expired
      if (result?.status === 404 && result?.error?.includes("Search ID")) {
        lastError = new Error("Cache miss - search results expired");
        lastError.status = 404;

        // Only retry with cache miss recovery if we have the required params
        if (attempt < maxRetries && searchParams && auth) {
          if (onCacheMissRecovery) {
            onCacheMissRecovery(attempt + 1, "Re-running search due to cache miss");
          }
          if (onRetry) {
            onRetry(attempt + 1, delay, "Cache miss - re-running search");
          }

          try {
            console.warn("Cache miss detected, re-running main search to populate cache...");

            // Notify user that we're recovering from cache miss
            toast.info("Search results expired. Re-running search...", {autoClose: 3000});

            // Re-run main search to populate cache
            // Use synchronous mode to ensure cache is populated before retrying
            await fetchSearch(
              searchParams,
              auth,
              {searchType: "search", useStreaming: false, waitForCompletion: true}
            );

            console.info("Search cache repopulated successfully, retrying operation...");
            toast.success("Search complete. Retrying operation...", {autoClose: 2000});

            // Wait before retrying the derivative operation
            await wait(delay);
            delay = Math.min(delay * backoffMultiplier, maxDelayMs);
            continue;
          } catch (searchError) {
            console.error("Failed to re-run search on cache miss:", searchError);
            toast.error("Failed to recover from expired search results");
            throw new SearchError("Failed to recover from cache miss: " + (searchError.message || String(searchError)));
          }
        } else if (!searchParams || !auth) {
          // Can't recover - missing required params
          throw new SearchError("Search results expired and cannot be recovered. Please re-run your search.");
        }
      }

      // Check for error status
      if (result?.status && result.status >= 400) {
        if (result.status === 413) {
          throw new SearchTimeoutError();
        }
        throw new SearchError(result.error || `Request failed with status ${result.status}`);
      }

      return result;
    } catch (error) {
      lastError = error;

      // Don't retry on client-side cancellation or explicit errors
      if (error instanceof SearchCanceled || error instanceof SearchError || error instanceof SearchTimeoutError) {
        throw error;
      }

      // Retry on network errors or unexpected failures
      if (attempt < maxRetries) {
        if (onRetry) {
          onRetry(attempt + 1, delay, error.message || "Network error");
        }
        await wait(delay);
        delay = Math.min(delay * backoffMultiplier, maxDelayMs);
        continue;
      }
    }
  }

  // All retries exhausted
  throw lastError || new Error("Operation failed after retries");
};

// PLEX-6255: graph, evidence chains, the dataset filter and export each have their own
// endpoint (rd PLEX-6261), which answers from a search POST /search has run, named by its
// search_id, and never starts one. So a result takes two calls: the search, then the result.

// How many times a result request runs its search again when the search's results expired
// between the two calls (a 404), before giving up.
const SEARCH_REFRESHES = 1;
// How many "search still running" (202) answers a result request rides out. Each comes after
// the server has waited up to its own MAX_LONGPOLL_WAIT for the search, so this bounds minutes.
const NOT_READY_RETRIES = 20;

// A request body without the fields that have no value: the endpoints refuse any field they
// don't declare, and a null where a string is declared.
const compact = (body) => Object.fromEntries(
  Object.entries(body).filter(([, v]) => v != null && !(Array.isArray(v) && v.length === 0)));

/**
 * PLEX-6255: run `searchParams`' search, or find it cached, waiting for it as the old single
 * call did, and return its search_id. One result per category: only the id is needed.
 */
const searchIdFor = async (searchParams, auth, {isCanceled, onProgress, signal}) => {
  const result = await fetchSearch(
    {...searchParams, pageSize: 1},
    auth,
    {searchType: "search", useStreaming: false, waitForCompletion: true, isCanceled, signal,
     ...(onProgress ? {onProgress} : {})}
  );
  if (!result?.search_id) {
    throw new SearchError("The search did not return a search_id");
  }
  return result.search_id;
};

/**
 * PLEX-6255: one result of `searchParams`' search, from its own endpoint (`url`, one of
 * ExternalRESTAPI.Export/Evidence/Network/Connected), with `body` as that endpoint's own
 * parameters. Resolves with the parsed JSON, or with the Response itself when the answer is a
 * file (an export).
 *
 * - The endpoint answers 202 while the search still runs (it has already waited for it):
 *   ask again, up to NOT_READY_RETRIES times.
 * - It answers 404 when the search's results have expired since POST /search ran it: run the
 *   search again, then ask again, SEARCH_REFRESHES times.
 */
export const fetchSearchResult = async (url, searchParams, body, auth, options = {}) => {
  const {isCanceled = () => false, onProgress = null, signal = null} = options;
  const canceled = () => isCanceled() || !!signal?.aborted;
  const searchOptions = {isCanceled: canceled, onProgress, signal};
  let searchID = await searchIdFor(searchParams, auth, searchOptions);
  let refreshes = 0;
  let notReady = 0;
  for (;;) {
    if (canceled()) {
      throw new SearchCanceled("Operation canceled");
    }
    let response;
    try {
      response = await new RequestBuilder(new URL(url, window.location.origin), signal ? {signal} : {})
        .asPOST(JSON.stringify(compact({search_id: searchID, ...body})))
        .withJSONContentType()
        .withAuthorization(auth)
        .fetch();
    } catch (error) {
      if (error?.status === 404 && refreshes < SEARCH_REFRESHES) {
        refreshes += 1;
        console.info("Search results expired; running the search again");
        searchID = await searchIdFor(searchParams, auth, searchOptions);
        continue;
      }
      throw error;
    }
    const contentType = response.headers?.get("content-type") || "";
    if (!contentType.includes("json")) {
      return response;  // a file: the export itself
    }
    const result = await response.json();
    if (result?.status === 202) {
      if (notReady >= NOT_READY_RETRIES) {
        throw new SearchTimeoutError(result.info || undefined);
      }
      notReady += 1;
      onProgress?.(result.progress ?? 0, result.total ?? 0, result.info || "");
      continue;
    }
    return result;
  }
};

/**
 * Filter search results by dataset sources.
 * Returns IDs of entities in the specified category that have evidence from the given datasets.
 *
 * Example: "Which disease results have evidence from ClinicalTrials.gov or PubMed?"
 *
 * @param {Object} searchParams - The search the results are from
 * @param {Array} datasetsFilter - Array of dataset keys to filter by (e.g., ['pubmed', 'clinicaltrials'])
 * @param {string} category - Category to filter (e.g., 'disease', 'compound')
 * @param {Object} auth - Authentication data
 * @param {Object} options - isCanceled, onRetry, and retry config for network errors
 * @returns {Promise<{ids: Array}>} - Promise resolving to filtered entity IDs
 */
export const fetchSearchByDataset = (searchParams, datasetsFilter, category, auth, options = {}) => {
  const {isCanceled = () => false, onRetry = null, onCacheMissRecovery: _unused, ...retryConfig} = options;
  return fetchWithRetry(
    () => fetchSearchResult(ExternalRESTAPI.Connected, searchParams,
                            {category, datasets_filter: datasetsFilter}, auth, {isCanceled}),
    {isCanceled, onRetry, ...retryConfig}
  );
};

/**
 * Fetch network graph data for visualization.
 *
 * @param {Object} searchParams - Search parameters including query IDs
 * @param {string} targetCategory - Category to generate graph for (or "all")
 * @param {Object} auth - Authentication data
 * @param {Object} options - isCanceled, onProgress, onRetry, and retry config for network errors
 */
export const fetchGraphData = (searchParams, targetCategory, auth, options = {}) => {
  const {isCanceled = () => false, onRetry = null, onProgress = null,
         onCacheMissRecovery: _unused, ...retryConfig} = options;
  return fetchWithRetry(
    () => fetchSearchResult(ExternalRESTAPI.Network, searchParams, {
      target_category: targetCategory,
      datasets_filter: searchParams?.datasetsFilter,
      // Not part of the search, so the search_id doesn't carry it (rd PLEX-6261).
      direct_connection_id: searchParams?.directConnectionID,
    }, auth, {isCanceled, onProgress}),
    {isCanceled, onRetry, ...retryConfig}
  );
};

/**
 * Fetch evidence chains between query and target.
 *
 * @param {Object} searchParams - Search parameters including query IDs
 * @param {string} targetID - Target ID to find evidence chains to
 * @param {Array} datasetsFilter - Optional dataset filter
 * @param {Object} auth - Authentication data
 * @param {Object} options - isCanceled, onProgress, signal, onRetry, and retry config
 */
export const fetchEChains = (searchParams, targetID, datasetsFilter, auth, options = {}) => {
  const {
    isCanceled = () => false,
    onRetry = null,
    onProgress = null,
    onCacheMissRecovery: _unused,
    signal = null,   // AbortSignal — cancels the in-flight request (PLEX-4234)
    ...retryConfig
  } = options;

  // Evidence chains must run the search with find_related on. A target chain is
  // "how does my search connect to this target", and the target is often a
  // sibling entity in the same category (e.g. BRAF -> RAF1) that a plain search
  // never surfaces — so it isn't in the result set and _search_echains 400s
  // ("target not found in search results"). find_related expands back to the
  // starting category (target -> compounds/diseases -> related targets), pulling
  // the target into scope so the chain resolves (e.g. BRAF -> Vemurafenib ->
  // RAF1). Force it on here regardless of the user's search setting. It is part of
  // the search, so it goes to POST /search, and the search_id carries it.
  const params = {...searchParams, findRelated: true};

  return fetchWithRetry(
    () => fetchSearchResult(ExternalRESTAPI.Evidence, params, {
      target_id: targetID,
      datasets_filter: datasetsFilter,
      direct_connection_id: searchParams?.directConnectionID,
    }, auth, {isCanceled, onProgress, signal}),
    {
      // Treat an abort as cancellation so fetchWithRetry bails at the top of
      // the next loop iteration instead of re-issuing the aborted request.
      isCanceled: signal ? () => signal.aborted || isCanceled() : isCanceled,
      onRetry,
      ...retryConfig
    }
  );
};

/**
 * POST to /search/activity to trigger on-demand compound target activity computation.
 * Streams newline-delimited JSON progress events; calls onProgress({phase, total, computed})
 * for each event and resolves with the final {status, computed, total} when done.
 */
export const fetchCompoundActivity = async (searchId, auth, onProgress = null) => {
  const url = new URL(ExternalRESTAPI.SearchActivity, window.location.origin);
  url.searchParams.set("search_id", searchId);
  const req = new RequestBuilder(url);
  const response = await req.withAuthorization(auth).withJSONContentType().asPOST("{}").fetch();

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let lastEvent = null;

  while (true) {
    const {done, value} = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, {stream: true});
    const lines = buffer.split("\n");
    buffer = lines.pop(); // keep incomplete last line
    for (const line of lines) {
      if (!line.trim()) continue;
      try {
        const event = JSON.parse(line);
        lastEvent = event;
        onProgress?.(event);
      } catch (e) {
        console.warn("fetchComputeActivity: failed to parse line", line, e);
      }
    }
  }
  // Flush any remaining buffer content
  if (buffer.trim()) {
    try {
      lastEvent = JSON.parse(buffer);
      onProgress?.(lastEvent);
    } catch (e) { /* ignore */ }
  }
  return lastEvent;
};

// PLEX-6259: is the turn behind `id` still running? `id` is a conversation id or a
// stream id. Replaces GET /api/agent/{id}/status. A conversation id answers from the
// conversation's live-turn state (X-Conversation-State), which stays "running" for the
// whole turn, as the old agent-alive record did, even after the stream's channel expired
// while the turn kept going (WEBAPP-3S); the channel's own status can't tell that apart
// from a dead producer, so it is used only for a stream with no conversation. Resolves
// true (running), false (ended or gone), or null (couldn't tell).
export const fetchTurnRunning = async (id, auth) => (await fetchTurnStatus(id, auth)).running;

// PLEX-6303: the same probe, with the turn's root agent. `running` is as fetchTurnRunning
// answers; `agentId` is the id the turn's events carry for its root agent (X-Agent-Id,
// sent to the conversation's owner only), which a page that reattaches to the turn after a
// reload has no other way to learn. Null when the backend doesn't send it.
export const fetchTurnStatus = async (id, auth) => {
  if (!id) return {running: null, agentId: null};
  const url = new URL(`${ExternalRESTAPI.Stream.replace("{streamId}", encodeURIComponent(id))}/status`,
                      window.location.origin);
  return new RequestBuilder(url).withAuthorization(auth).asGET()
    .fetch()
    .then(r => {
      const agentId = r.headers?.get("X-Agent-Id") || null;
      const state = r.headers?.get("X-Conversation-State");
      if (state) return {running: state === "running", agentId};
      const status = r.headers?.get("X-Stream-Status");
      return {running: status ? status === "active" : null, agentId};
    })
    .catch(() => ({running: null, agentId: null}));
};

// PLEX-6259: stop a stream's live turn and every sub-agent it started. `id` is a
// conversation id (it resolves to that conversation's live turn, for its owner) or a
// stream id. `turn`, when given, is a precondition: the backend cancels only while that
// stream is still the live turn, so a stop meant for a turn that has ended can't land
// on the next. Replaces POST /api/agent/{id}/cancel, which had to be sent once per
// sub-agent. Resolves true when the request went through, false otherwise; it never
// throws, because a cancel must never break the caller's own teardown.
export const cancelStream = async (id, auth, turn = null) => {
  if (!id) return false;
  const url = new URL(ExternalRESTAPI.Stream.replace("{streamId}", encodeURIComponent(id)),
                      window.location.origin);
  if (turn && turn !== id) {
    url.searchParams.set("turn", turn);
  }
  return new RequestBuilder(url).withAuthorization(auth).asDELETE()
    .fetch()
    .then(() => true)
    .catch(() => false);
};

export const fetchSearch = (searchParams, auth, params = {}) => {
  const {
    searchType = "search",
    isCanceled = () => false,
    onProgress = () => {},
    // If false, return immediately with a searchID; otherwise return once search results are ready
    waitForCompletion = true,
    // If true, use streaming mode (returns immediately with stream_id)
    useStreaming = false,
    // Extra params appended to URL after constructSearchAPIRequest (bypasses IGNOREPARAMS)
    extraParams = null,
    // Optional AbortSignal passed straight to the underlying fetch (PLEX-4234)
    signal = null,
    // The endpoint to POST to; a chat turn has its own (PLEX-6261, fetchChatCompletion)
    url = ExternalRESTAPI.Search,
  } = params;

  // Enable streaming by setting stream=true in search params
  const effectiveSearchParams = useStreaming
    ? {...searchParams, stream: true}
    : searchParams;

  // If `wait` is true, only returns when initial search results become available
  // Otherwise returns as soon as the searchID is obtained
  const req = constructSearchAPIRequest(
    url, effectiveSearchParams, false, signal ? {signal} : {});
  if (extraParams) {
    Object.entries(extraParams).forEach(([k, v]) => req.url.searchParams.set(k, v));
  }
  return req
    .withJSONContentType()
    .withAuthorization(auth)
    .fetch()
    .then(toJSON)
    .then((result) => {
      if (isCanceled()) {
        throw new SearchCanceled("Search canceled");
      }
      const {
        id: searchID = null,
        status = null,
        page_token: pageToken = null,
        results = null,
        stream_id: streamID = null,
      } = result;
      if (!waitForCompletion) {
        return {...(searchID ? {searchID} : {}), ...result};
      }
      if (status && status >= 400) {
        if (status === 413) {
          throw new SearchTimeoutError();
        }
        throw new SearchError(result.error);
      }
      // If streaming is enabled and we got a stream_id, return immediately
      if (streamID) {
        return result;
      }
      if (results || status === 200) {
        return {...(searchType === "search" ? {pageToken} : {}), ...result};
      }
      // Legacy polling mode: continue polling for results
      onProgress(result.progress, result.total, result.info || "");
      // Ensure we don't continually pass the nocache flag
      delete req.url.searchParams.delete('nocache');
      // Make sure we use the most recent auth available...
      return wait(5000).then(() => fetchSearch(searchParams, req.auth, {searchType, isCanceled, onProgress, signal, url}));
    })
};

/**
 * Export search results in various formats: the results themselves (POST /export), or the
 * evidence chains to `params.targetID` (POST /evidence), from the search `params` describe
 * (PLEX-6255). Resolves with the Response, a file.
 *
 * @param {Object} params - Search parameters (plus targetID for an evidence-chain export)
 * @param {Object} auth - Authentication data
 * @param {string} format - Export format (csv, xlsx, svg, etc.)
 * @param {string} _baseURL - Unused: each export has its own endpoint now
 * @param {Object} options - isCanceled, onRetry, and retry config for network errors
 */
export const exportSearchResults = async (params, auth, format = "svg", _baseURL = undefined, options = {}) => {
  const {isCanceled = () => false, onRetry = null, onCacheMissRecovery: _unused, ...retryConfig} = options;
  const {targetID, exportFormat: _format, full: _full, ...searchParams} = params;
  const own = {
    export_format: format,
    labels: getCustomLabels(),
    direct_connection_id: params.directConnectionID,
  };
  const [url, body] = targetID
    ? [ExternalRESTAPI.Evidence, {...own, target_id: targetID}]
    : [ExternalRESTAPI.Export, own];
  return fetchWithRetry(
    () => fetchSearchResult(url, searchParams, body, auth, {isCanceled}),
    {isCanceled, onRetry, ...retryConfig}
  );
};

/**
 * Check which molecules match a substructure query.
 * @param {string} query - SMARTS or SMILES substructure pattern
 * @param {string[]} molecules - Array of SMILES strings to test
 * @param {object} auth - Auth object with token
 * @returns {Promise<number[]>} Indices of matching molecules
 */
export const fetchSubstructureMatches = (query, molecules, auth) => {
  return new RequestBuilder(ExternalRESTAPI.MoleculeMatch + "/batch")
    .method("POST")
    .auth(auth)
    .body({ query, molecules })
    .fetch()
    .then(toJSON)
    .then(data => data.matches || []);
};